/**
 * Loads the Monaco editor bundle on demand and mounts it over a textarea.
 *
 * The bundle is built by the sibling `editor/` package into `public/editor/`,
 * which Vite copies verbatim into `dist/`. It is deliberately *not* part of
 * this app's module graph: it is several megabytes of VS Code services, and a
 * device that never mounts the editor never fetches a byte of it. That is also
 * why the import below is opaque to the bundler — see loadEditorModule().
 */

/** The subset of the editor module's return value this app uses. */
interface MountedEditor {
  readonly dispose: () => void;
  readonly focus: () => void;
}

interface EditorModule {
  readonly mountEditor: (options: {
    textarea: HTMLTextAreaElement;
    noteId?: string;
    onSave?: () => void;
  }) => Promise<MountedEditor>;
}

/** Dispatched on the body textarea to ask the mounted editor to take focus. */
export const BODY_FOCUS_EVENT = "vineelernote:focus-body";

export interface RichEditorOptions {
  readonly textarea: HTMLTextAreaElement;
  /** Gives the editor's model a stable URI. Absent when creating a new note. */
  readonly noteId?: string;
  /** Invoked on Cmd-S / Ctrl-S. */
  readonly onSave?: () => void;
}

export interface RichEditorHandle {
  /** Safe to call before the module has finished loading, and more than once. */
  readonly dispose: () => void;
}

/**
 * Served path, not a source path: the file lives in public/ and does not exist
 * until the editor package is built.
 *
 * This is resolved to an **absolute** URL before importing, and that is
 * load-bearing rather than cosmetic. Vite's dev server rewrites every dynamic
 * import to `__vite__injectQuery(url, 'import')`, which appends `?import` to
 * the request. A query turns what would be a static public-directory fetch into
 * a request through the transform pipeline, and Vite refuses to transform
 * anything in public/ — so `import("/editor/editor.js")` fails at runtime with
 * "This file is in /public and will be copied as-is during build ... should not
 * be imported from source code."
 *
 * injectQuery's first line is `if (url[0] !== "." && url[0] !== "/") return url`,
 * so an absolute URL passes through untouched and the browser fetches the plain
 * path that public/ actually serves.
 *
 * `@vite-ignore` only silences the unresolvable-import warning; it does not
 * prevent the rewrite. It is kept to stay quiet, not because it fixes this.
 *
 * The production build does not use injectQuery at all — which is exactly why
 * this bug is invisible to `vite build` and only appears in `npm run dev`.
 */
const EDITOR_MODULE_PATH = "/editor/editor.js";

function editorModuleUrl(): string {
  return new URL(EDITOR_MODULE_PATH, window.location.origin).href;
}

/**
 * Monaco's own FAQ answers "supported in mobile browsers?" with a flat no, and
 * on iOS every browser is WebKit. Capability query rather than UA sniffing:
 * a real keyboard and a pointer that can hover.
 */
const RICH_EDITOR_MEDIA = "(min-width: 900px) and (pointer: fine)";

/**
 * Whether to attempt the rich editor at all.
 *
 * `?plain=1` forces the textarea. The router is hash-based, so that lives in
 * the hash query (`#/notes/x/edit?plain=1`) and reaches here as the page's
 * parsed query — not `location.search`, which is always empty in this app.
 */
export function wantsRichEditor(query: URLSearchParams): boolean {
  if (query.has("plain")) {
    return false;
  }
  return window.matchMedia(RICH_EDITOR_MEDIA).matches;
}

async function loadEditorModule(): Promise<EditorModule> {
  const url = editorModuleUrl();
  return (await import(/* @vite-ignore */ url)) as EditorModule;
}

/**
 * Mounts the rich editor over `textarea`, asynchronously.
 *
 * Returns immediately with a handle; the module loads in the background. The
 * caller can dispose before loading finishes and the editor will never appear.
 *
 * Failures are swallowed to a console warning **on purpose**: the textarea is
 * still there, fully functional, and losing syntax highlighting is a far better
 * outcome than losing the ability to edit a note. The cost of that choice is
 * that a missing or broken bundle is invisible in the UI — which is why
 * `devops/build-prod` fails the build if the bundle is absent, rather than
 * relying on anyone noticing at runtime.
 */
export function attachRichEditor(options: RichEditorOptions): RichEditorHandle {
  let mounted: MountedEditor | null = null;
  let disposed = false;

  const onFocusRequest = (): void => mounted?.focus();
  options.textarea.addEventListener(BODY_FOCUS_EVENT, onFocusRequest);

  void (async (): Promise<void> => {
    try {
      const module = await loadEditorModule();
      if (disposed) {
        return;
      }
      const editor = await module.mountEditor({
        textarea: options.textarea,
        // exactOptionalPropertyTypes: an absent key and an undefined one are
        // different types here, so the optionals are spread in conditionally.
        ...(options.noteId === undefined ? {} : { noteId: options.noteId }),
        ...(options.onSave === undefined ? {} : { onSave: options.onSave }),
      });
      if (disposed) {
        editor.dispose();
        return;
      }
      mounted = editor;
    } catch (error: unknown) {
      console.warn("rich editor unavailable, using the textarea", error);
    }
  })();

  return {
    dispose(): void {
      disposed = true;
      options.textarea.removeEventListener(BODY_FOCUS_EVENT, onFocusRequest);
      mounted?.dispose();
      mounted = null;
    },
  };
}
